import React, { useEffect, useState } from "react"
import moment from "moment"
import { db } from "../../../../utils/firebase"
import { CommentIcon, AlertIcon } from "../../../../utils/svgs"

import { ACTIVITY_TYPES, SYSTEM_LOG_TYPES } from "./types"

// Types this log surfaces. Combines the system events (status, agent,
// tag, follow-up, quote changes) with NOTE entries — gives a complete
// audit of "what's been done to this enquiry" in one place. The note
// body itself stays in the Activity tab; this view shows the metadata.
const LOG_TYPES = [...SYSTEM_LOG_TYPES, "NOTE"]

/**
 * Resolve a uid → display name. Lightweight per-session cache so a long
 * change log doesn't pound Firestore — same pattern as the activity feed.
 */
const userNameCache = {}
const resolveUserName = async (uid) => {
	if (!uid) return null
	if (userNameCache[uid]) return userNameCache[uid]
	const doc = await db.doc(`users/${uid}`).get()
	if (doc.exists) {
		const { first_name, last_name } = doc.data()
		const name = `${first_name || ""} ${last_name || ""}`.trim() || null
		userNameCache[uid] = name
		return name
	}
	return null
}

/**
 * Read-only audit log for the enquiry. Surfaces system-level events
 * (status / agent / tag / follow-up / quote changes) plus a record of
 * each note that's been added, so you can see "everything that's
 * happened" to this enquiry in chronological order.
 *
 * Reads from the same `enquiries/{id}/activity` subcollection as the
 * Activity feed — no data migration. Filters client-side so we don't
 * need a Firestore composite index for the IN-query.
 */
export default function ChangeLog({ basePath }) {
	const [entries, setEntries] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [expanded, setExpanded] = useState(false)

	useEffect(() => {
		if (!basePath) return
		const unsub = db
			.collection(`${basePath}/activity`)
			.orderBy("created", "desc")
			.onSnapshot(
				(snap) => {
					const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
					setEntries(all.filter((e) => LOG_TYPES.includes(e.type)))
					setLoading(false)
					setError(null)
				},
				(err) => {
					console.error("Log listener error:", err)
					setError(err.message || "Could not load log.")
					setLoading(false)
				}
			)
		return () => unsub()
	}, [basePath])

	const visibleEntries = expanded ? entries : entries.slice(0, 20)
	const hiddenCount = entries.length - visibleEntries.length

	return (
		<section className="enquiry-log-section">
			{loading && (
				<div className="enquiry-feed-empty" role="status" aria-live="polite">
					<small>Loading log…</small>
				</div>
			)}

			{!loading && error && (
				<div className="enquiry-feed-empty" role="alert">
					<span className="enquiry-feed-empty-icon" aria-hidden="true">
						<AlertIcon />
					</span>
					<small>Couldn't load log: {error}</small>
				</div>
			)}

			{!loading && !error && entries.length === 0 && (
				<div className="enquiry-feed-empty">
					<span className="enquiry-feed-empty-icon" aria-hidden="true">
						<CommentIcon />
					</span>
					<small>No log entries yet — changes and notes will show up here.</small>
				</div>
			)}

			{!loading && !error && entries.length > 0 && (
				<>
					<ul className="enquiry-log-list">
						{visibleEntries.map((entry) => (
							<LogEntry key={entry.id} entry={entry} />
						))}
					</ul>

					{hiddenCount > 0 && (
						<button
							type="button"
							className="enquiry-log-toggle"
							onClick={() => setExpanded(true)}>
							Show {hiddenCount} older {hiddenCount === 1 ? "entry" : "entries"}
						</button>
					)}

					{expanded && entries.length > 20 && (
						<button
							type="button"
							className="enquiry-log-toggle"
							onClick={() => setExpanded(false)}>
							Show fewer
						</button>
					)}
				</>
			)}
		</section>
	)
}

function LogEntry({ entry }) {
	const [authorName, setAuthorName] = useState(null)

	useEffect(() => {
		let cancelled = false
		resolveUserName(entry.user).then((name) => {
			if (!cancelled) setAuthorName(name)
		})
		return () => {
			cancelled = true
		}
	}, [entry.user])

	const meta = ACTIVITY_TYPES[entry.type] || { label: entry.type }
	const created = entry.created?.seconds ? moment(entry.created.seconds, "X") : null
	const relative = created ? created.fromNow() : ""
	const timestamp = created ? created.format("DD/MM/YYYY HH:mm") : "—"

	// For system events the body is human-written ("Status changed from
	// Hot to Processing"); for NOTE the body is the user's note content,
	// which lives properly on the Activity tab. Here we just want a
	// metadata-style record that "a note was added", with the first line
	// or so as a hint.
	let description
	if (entry.type === "NOTE") {
		const preview = (entry.body || "").trim().split("\n")[0]
		const truncated = preview.length > 80 ? preview.slice(0, 80) + "…" : preview
		description = preview ? `Note added: "${truncated}"` : "Note added"
	} else {
		description = entry.body || meta.label
	}

	return (
		<li className="enquiry-log-entry" title={relative}>
			<span className="enquiry-log-dot" aria-hidden="true" />
			<span className="enquiry-log-body">{description}</span>
			<span className="enquiry-log-meta">
				<span className="enquiry-log-author">
					{authorName || <span className="no-value">Unknown</span>}
				</span>
				<span className="enquiry-log-meta-sep" aria-hidden="true">·</span>
				<span className="enquiry-log-time">{timestamp}</span>
			</span>
		</li>
	)
}
